import { useEffect, useState } from "react";
import { X, Link2, RefreshCw, Ban, Trash2, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { nok } from "@/pages/admin/budsjett/budsjettUtils";

const Seksjon = ({ label, children }) => (
  <div className="mt-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const Felt = ({ label, verdi, onLagre, testid, textarea, type = "text", placeholder }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="adm-label">
      {label}
      <Tag
        value={v}
        type={textarea ? undefined : type}
        rows={textarea ? 5 : undefined}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => String(v) !== String(verdi ?? "") && onLagre(v)}
        className="adm-input"
        data-testid={testid}
      />
    </label>
  );
};

const OverrideTall = ({ label, verdi, standard, onLagre, testid }) => (
  <label className="adm-label">
    {label}
    <input
      type="number"
      defaultValue={verdi ?? ""}
      placeholder={`Std: ${standard}`}
      onBlur={(e) => {
        const ny = e.target.value === "" ? null : Number(e.target.value);
        if (ny !== (verdi ?? null)) onLagre(ny);
      }}
      className="adm-input"
      data-testid={testid}
    />
  </label>
);

const OverrideBool = ({ label, verdi, standard, onLagre, testid }) => {
  const eff = verdi ?? standard;
  return (
    <button
      type="button"
      onClick={() => onLagre(!eff)}
      className={`inline-flex items-center gap-2 text-[12px] font-medium rounded-lg px-2.5 py-2 border transition-colors ${
        eff ? "bg-neutral-900 text-white border-neutral-900" : "text-neutral-500 border-[#DED9CD] hover:text-neutral-800"
      }`}
      data-testid={testid}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${eff ? "bg-[#F1E75A]" : "bg-neutral-300"}`} />
      {label}{verdi != null && verdi !== standard ? " *" : ""}
    </button>
  );
};

export default function ProposalEditor({ proposal, products, onPatch, onRegenerate, onRevoke, onDelete, onLukk }) {
  const [nyExtra, setNyExtra] = useState("");

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); onLukk(); } };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [onLukk]);

  const prod = products.find((p) => p.key === proposal.recommendedKey) || {};
  const std = prod.deliverables || {};
  const ov = proposal.deliverableOverrides || {};
  const setOv = (k, v) => {
    const neste = { ...ov };
    if (v === null || v === undefined) delete neste[k];
    else neste[k] = v;
    onPatch({ deliverableOverrides: neste });
  };

  const shareUrl = `${window.location.origin}${proposal.sharePath}`;
  const kopier = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Proposal-lenken er kopiert");
  };

  const leggTilExtra = () => {
    const t = nyExtra.trim();
    if (!t) return;
    onPatch({ extras: [...(proposal.extras || []), t] });
    setNyExtra("");
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="proposal-editor">
      <div className="sak-backdrop absolute inset-0 bg-black/30" onClick={onLukk} />
      <aside className="sak-drawer relative w-full max-w-[640px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">Commercial proposal</p>
              <h2 className="text-[19px] font-semibold leading-tight mt-1" data-testid="proposal-editor-tittel">
                {proposal.effectiveName} · {proposal.companyName || proposal.preparedFor}
              </h2>
              <p className="text-[12px] text-neutral-400 mt-0.5 tabular-nums">
                {nok(proposal.effectivePrice)} eks. mva · {proposal.active ? "Aktiv lenke" : "Revokert"}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="proposal-slett-btn">
                <Trash2 size={15} />
              </button>
              <button type="button" onClick={onLukk} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="proposal-editor-lukk">
                <X size={17} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Seksjon label="Delingslenke (fortrolig 1:1)">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={kopier} className="adm-btn" data-testid="proposal-kopier-lenke">
                <Link2 size={13} /> Kopier lenke
              </button>
              <a href={shareUrl} target="_blank" rel="noreferrer" className="adm-btn-ghost adm-btn-sm" data-testid="proposal-aapne-lenke">
                <ExternalLink size={12} /> Åpne
              </a>
              <button type="button" onClick={onRegenerate} className="adm-btn-ghost adm-btn-sm" data-testid="proposal-regenerer-btn">
                <RefreshCw size={12} /> Ny lenke
              </button>
              {proposal.active && (
                <button type="button" onClick={onRevoke} className="adm-btn-ghost adm-btn-sm text-red-600" data-testid="proposal-revoker-btn">
                  <Ban size={12} /> Revoker
                </button>
              )}
            </div>
            <p className="text-[11px] text-neutral-400 mt-2 break-all">{shareUrl}</p>
          </Seksjon>

          <Seksjon label="Anbefalt partnerskap">
            <div className="grid grid-cols-2 gap-3">
              <label className="adm-label">
                Produkt
                <select
                  value={proposal.recommendedKey}
                  onChange={(e) => onPatch({ recommendedKey: e.target.value })}
                  className="adm-input"
                  data-testid="proposal-produkt-select"
                >
                  {products.map((p) => (
                    <option key={p.key} value={p.key}>{p.name} — {nok(p.price)}</option>
                  ))}
                </select>
              </label>
              <label className="adm-label">
                Alternativ (valgfritt)
                <select
                  value={proposal.altKey || ""}
                  onChange={(e) => onPatch({ altKey: e.target.value || null })}
                  className="adm-input"
                  data-testid="proposal-alt-select"
                >
                  <option value="">Ingen</option>
                  {products.filter((p) => p.key !== proposal.recommendedKey).map((p) => (
                    <option key={p.key} value={p.key}>{p.name} — {nok(p.price)}</option>
                  ))}
                </select>
              </label>
              <Felt label="Egendefinert tittel (valgfritt)" verdi={proposal.titleOverride} placeholder={prod.name} onLagre={(v) => onPatch({ titleOverride: v })} testid="proposal-tittel-override" />
              <OverrideTall label="Pris eks. mva (overstyr)" verdi={proposal.priceOverride} standard={nok(prod.price || 0)} onLagre={(v) => onPatch({ priceOverride: v })} testid="proposal-pris-override" />
            </div>
          </Seksjon>

          <Seksjon label="Leveranser (overstyr standarden ved behov — * = overstyrt)">
            <div className="grid grid-cols-2 gap-3">
              <OverrideTall label="Billetter" verdi={ov.physicalTickets} standard={std.physicalTickets ?? 0} onLagre={(v) => setOv("physicalTickets", v)} testid="proposal-ov-tickets" />
              <OverrideTall label="Middagsplasser" verdi={ov.dinnerSeats} standard={std.dinnerSeats ?? 0} onLagre={(v) => setOv("dinnerSeats", v)} testid="proposal-ov-dinner" />
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <OverrideBool label="Expo-aktivering" verdi={ov.expoActivation} standard={!!std.expoActivation} onLagre={(v) => setOv("expoActivation", v)} testid="proposal-ov-expo" />
              <OverrideBool label="Kategorieksklusivitet" verdi={ov.categoryExclusivity} standard={!!std.categoryExclusivity} onLagre={(v) => setOv("categoryExclusivity", v)} testid="proposal-ov-exclusivity" />
              <OverrideBool label="Executive roundtable" verdi={ov.executiveRoundtable} standard={!!std.executiveRoundtable} onLagre={(v) => setOv("executiveRoundtable", v)} testid="proposal-ov-roundtable" />
            </div>
            <label className="adm-label mt-3">
              Broadcast-synlighet
              <select
                value={ov.broadcastVisibility ?? std.broadcastVisibility ?? "standard"}
                onChange={(e) => setOv("broadcastVisibility", e.target.value)}
                className="adm-input"
                data-testid="proposal-ov-broadcast"
              >
                <option value="none">Ingen</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </label>
            <div className="mt-3">
              <p className="text-[11px] text-neutral-400 mb-1.5">Egendefinerte punkter i «What it includes»</p>
              {(proposal.extras || []).map((t, i) => (
                <div key={`${t}-${i}`} className="flex items-center gap-2 py-0.5 group" data-testid={`proposal-extra-${i}`}>
                  <span className="w-1 h-1 rounded-full bg-neutral-400 shrink-0" />
                  <span className="flex-1 text-[12.5px] text-neutral-800">{t}</span>
                  <button
                    type="button"
                    onClick={() => onPatch({ extras: (proposal.extras || []).filter((_, x) => x !== i) })}
                    className="opacity-0 group-hover:opacity-100 text-neutral-300 hover:text-red-500 transition-all"
                    data-testid={`proposal-extra-slett-${i}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2 mt-1">
                <Plus size={13} className="text-neutral-300 shrink-0" />
                <input
                  value={nyExtra}
                  onChange={(e) => setNyExtra(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); leggTilExtra(); } }}
                  placeholder="F.eks. «50 tickets total» eller «Keynote dinner speech» — Enter"
                  className="flex-1 text-[12.5px] py-1 bg-transparent text-neutral-900 focus:outline-none placeholder:text-neutral-300"
                  data-testid="proposal-ny-extra-input"
                />
              </div>
            </div>
          </Seksjon>

          <Seksjon label="Why this partnership (avsnitt skilles med blank linje)">
            <Felt label="" verdi={proposal.whyText} onLagre={(v) => onPatch({ whyText: v })} testid="proposal-why-text" textarea />
          </Seksjon>

          <Seksjon label="Activation concept">
            <Felt label="" verdi={proposal.activationText} onLagre={(v) => onPatch({ activationText: v })} testid="proposal-activation-text" textarea />
          </Seksjon>

          <Seksjon label="Vilkår og kontakt">
            <div className="grid grid-cols-2 gap-3">
              <Felt label="Betalingsplan (tekst)" verdi={proposal.paymentPlanText} onLagre={(v) => onPatch({ paymentPlanText: v })} testid="proposal-betalingsplan" />
              <Felt label="Gyldig til" verdi={proposal.validUntil} type="date" onLagre={(v) => onPatch({ validUntil: v })} testid="proposal-gyldig-til" />
              <Felt label="Kontaktperson" verdi={proposal.contactName} onLagre={(v) => onPatch({ contactName: v })} testid="proposal-kontakt-navn" />
              <Felt label="Kontakt-epost" verdi={proposal.contactEmail} onLagre={(v) => onPatch({ contactEmail: v })} testid="proposal-kontakt-epost-input" />
              <Felt label="«Prepared for» (visningsnavn)" verdi={proposal.preparedFor} onLagre={(v) => onPatch({ preparedFor: v })} testid="proposal-prepared-for-input" />
            </div>
          </Seksjon>

          {(proposal.activity || []).length > 0 && (
            <Seksjon label="Aktivitet">
              <div className="space-y-1">
                {[...proposal.activity].reverse().slice(0, 8).map((a, i) => (
                  <p key={i} className="text-[11px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">{new Date(a.at).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}</span>
                  </p>
                ))}
              </div>
            </Seksjon>
          )}
        </div>
      </aside>
    </div>
  );
}
